import { readFile, isPdf, isImage } from '../lib/util.ts';

export interface DropItem {
  name: string;
  size: number;
  bytes: Uint8Array;
  type?: string;
}

export interface FileDropOptions {
  accept: 'pdf' | 'image' | 'any';
  multiple?: boolean;
  onFiles: (files: File[]) => void;
}

/** 单个文件上限：超过该大小的文件会被 toDropItems 拒绝（100MB 是浏览器内存的安全带） */
export const MAX_FILE_BYTES = 100 * 1024 * 1024;

/** 同名去重上限：同一名字最多保留 9 份（后续同名用 (2)(3) 后缀） */
const DEDUPE_CAP = 9;

export function setupDropZone(el: HTMLElement, opts: FileDropOptions) {
  const input = document.createElement('input');
  input.type = 'file';
  input.multiple = opts.multiple ?? true;
  input.accept =
    opts.accept === 'pdf' ? 'application/pdf' : opts.accept === 'image' ? 'image/*' : '*';

  el.addEventListener('click', () => input.click());

  el.addEventListener('dragover', (e) => {
    e.preventDefault();
    el.classList.add('drag');
  });
  el.addEventListener('dragleave', () => el.classList.remove('drag'));
  el.addEventListener('drop', (e) => {
    e.preventDefault();
    el.classList.remove('drag');
    if (e.dataTransfer?.files.length) opts.onFiles(filterFiles(e.dataTransfer.files, opts.accept));
  });

  input.addEventListener('change', () => {
    if (input.files?.length) opts.onFiles(filterFiles(input.files, opts.accept));
    input.value = '';
  });
}

function filterFiles(fileList: FileList, accept: 'pdf' | 'image' | 'any') {
  return Array.from(fileList).filter((f) => {
    if (f.size > MAX_FILE_BYTES) {
      console.warn(`[pdf-toolkit] 拒绝超 ${MAX_FILE_BYTES / 1024 / 1024}MB 的文件：${f.name}`);
      return false;
    }
    if (accept === 'pdf') return isPdf(f);
    if (accept === 'image') return isImage(f);
    return true;
  });
}

/**
 * 读取文件内容，做两件防御：
 * 1. 同名文件加 (n) 后缀（a.pdf → a.pdf, a (2).pdf, a (3).pdf …）
 * 2. 超限大小直接返回 null 并由调用方过滤
 */
export async function toDropItems(files: File[]): Promise<DropItem[]> {
  // 并行读所有文件字节（顺序 await 在多文件场景下慢好几倍）
  const results = await Promise.all(
    files.map(async (f) => {
      if (f.size > MAX_FILE_BYTES) return null as DropItem | null;
      return {
        name: f.name,
        size: f.size,
        bytes: await readFile(f),
        type: f.type || undefined,
      } as DropItem;
    }),
  );
  // 同名去重：a.pdf, a (2).pdf, a (3).pdf … 最多 9 份同名
  const nameCount = new Map<string, number>();
  const out: DropItem[] = [];
  for (const r of results) {
    if (!r) continue;
    const seen = nameCount.get(r.name) ?? 0;
    nameCount.set(r.name, seen + 1);
    if (seen >= DEDUPE_CAP) continue;
    if (seen === 0) {
      out.push(r);
    } else {
      const stem = r.name.replace(/\.[^.]+$/, '');
      const ext = r.name.match(/(\.[^.]+)$/) ? r.name.slice(r.name.lastIndexOf('.')) : '';
      out.push({ ...r, name: `${stem} (${seen + 1})${ext}` });
    }
  }
  return out;
}
